import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { getConversations } from '../lib/api';

/**
 * useGuestHubArrivalToast
 * -----------------------
 * Lightweight polling hook that watches for NEW_REQUEST conversations
 * (GuestHub intake) and shows a toast when previously-unseen ones arrive.
 *
 *   - First load is treated as the baseline (no toast).
 *   - Default poll interval: 30s.
 *   - Tab visibility is honoured: pauses polling when the tab is hidden,
 *     resumes on visibility change.
 *
 * The toast deep-links to Guest Inbox with the conversation selected.
 *
 * @param {Object}  opts
 * @param {boolean} opts.enabled              Pause toggle (default true)
 * @param {string=} opts.propertyId           Scope to a single property
 * @param {number=} opts.intervalMs           Poll cadence (default 30_000)
 */
export const useGuestHubArrivalToast = ({
  enabled = true,
  propertyId = undefined,
  intervalMs = 30000,
} = {}) => {
  const navigate = useNavigate();
  const knownIds = useRef(null);   // Set<string> | null (null = uninitialised)
  const isMounted = useRef(true);
  const timerRef = useRef(null);

  useEffect(() => {
    isMounted.current = true;
    if (!enabled) return () => { isMounted.current = false; };

    const pollOnce = async () => {
      try {
        const params = {};
        if (propertyId && propertyId !== 'all') params.propertyId = propertyId;
        const res = await getConversations(params);
        const list = Array.isArray(res?.data) ? res.data : [];
        const incoming = list.filter((c) => c?.intake_status === 'NEW_REQUEST');

        if (knownIds.current === null) {
          // First poll → just record baseline, no toast.
          knownIds.current = new Set(incoming.map((c) => c.id));
          return;
        }

        const fresh = incoming.filter((c) => !knownIds.current.has(c.id));
        fresh.forEach((c) => {
          knownIds.current.add(c.id);
        });

        if (fresh.length === 1 && isMounted.current) {
          const c = fresh[0];
          const reqType = (c.request_type || c.intake_category || 'request').toString().replace(/_/g, ' ').toLowerCase();
          const guest = c.guestName || 'a guest';
          toast.info('New Guest Hub request', {
            description: `${guest} — ${reqType}`,
            duration: 8000,
            action: {
              label: 'Open',
              onClick: () => navigate(`/desk/inbox?conversation=${c.id}`),
            },
          });
        } else if (fresh.length > 1 && isMounted.current) {
          toast.info(`${fresh.length} new Guest Hub requests`, {
            description: 'Open Guest Inbox to triage.',
            duration: 8000,
            action: { label: 'Open', onClick: () => navigate('/desk/inbox') },
          });
        }
      } catch {
        // Network blip — try again on the next tick.
      }
    };

    // Initial baseline + recurring poll.
    pollOnce();
    timerRef.current = setInterval(() => {
      if (document.visibilityState === 'visible') pollOnce();
    }, intervalMs);

    const onVisChange = () => {
      if (document.visibilityState === 'visible') pollOnce();
    };
    document.addEventListener('visibilitychange', onVisChange);

    return () => {
      isMounted.current = false;
      if (timerRef.current) clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', onVisChange);
    };
  }, [enabled, propertyId, intervalMs, navigate]);
};

export default useGuestHubArrivalToast;
